<template>
  <div class="organization-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <span class="header-title">{{
            $t("message.organization.title")
          }}</span>
          <el-button v-if="canAddOrganization" type="primary" @click="handleAdd">{{
            $t("message.organization.add")
          }}</el-button>
        </div>
      </template>

      
          <div class="right-content">
            <!-- Search Form -->
            <div class="search-area">

              <el-form :inline="true">

                <el-form-item :label="$t('message.organization.company')" prop="company_id">
                  <el-tree-select
                    v-model="formData.company_id"
                    :data="companyTreeData"                
                    :props="treeProps"
                    :placeholder="$t('message.organization.selectCompany')"
                    check-strictly
                    clearable
                    filterable
                    style="width: 200px"
                    @node-click="handleNodeClick"
                    @change="handleCompanyChange"
                  />
                </el-form-item>

                <el-form-item :label="$t('message.organization.name')">
                  <el-input
                    v-model="queryParams.name"
                    :placeholder="$t('message.organization.namePlaceholder')"
                    clearable
                    @clear="handleQuery"
                    @keyup.enter="handleQuery"
                    style="width: 200px"
                  />
                </el-form-item>

                <el-form-item>
                  <el-button type="primary" @click="handleQuery">{{
                    $t("message.common.search")
                  }}</el-button>
                </el-form-item>

              </el-form>
            </div>

            <!-- Data Table -->
            <div class="table-wrapper">
              <el-table
                :data="organizationList"
                border
                v-loading="loading"
                style="width: 100%"
              >
                <el-table-column
                  v-if="isColumnVisible"
                  prop="id"
                  :label="$t('message.organization.id')"
                  width="80"
                />
                <el-table-column
                  prop="name"
                  :label="$t('message.organization.name')"
                  width="220"
                  sortable
                />
                <el-table-column
                  prop="parent_name"
                  :label="$t('message.organization.parent')"
                  width="220"
                  sortable
                >
                  <template #default="{ row }">
                    {{ row.parent_name || "--" }}
                  </template>
                </el-table-column>

                <el-table-column
                  prop="contact"
                  :label="$t('message.organization.contact')"
                  width="130"
                  sortable
                >
                  <template #default="{ row }">
                    {{ row.contact || "--" }}
                  </template>
                </el-table-column>
                <el-table-column
                  prop="phone"
                  :label="$t('message.organization.phone')"
                  width="130"
                  sortable
                >
                  <template #default="{ row }">
                    {{ row.phone || "--" }}
                  </template>
                </el-table-column>

                <el-table-column
                  prop="location"
                  :label="$t('message.organization.location')"
                  width="150"
                  sortable
                >
                  <template #default="{ row }">
                    {{ row.location || "--" }}
                  </template>
                </el-table-column>

                <el-table-column
                  prop="describe"
                  :label="$t('message.organization.describe')"
                  width="230"
                  sortable
                >
                  <template #default="{ row }">
                    {{ row.describe || "--" }}
                  </template>
                </el-table-column>
                <el-table-column
                  prop="created_at"
                  :label="$t('message.organization.createdAt')"
                  width="180"
                  sortable
                >
                  <template #default="{ row }">
                    {{ formatDateTime(row.created_at) || "--" }}
                  </template>
                </el-table-column>
                <el-table-column
                  :label="$t('message.organization.actions')"
                  width="150"
                  fixed="right"
                >
                  <template #default="{ row }">
                    <el-button
                     v-if="canEditOrganization" 
                      link
                      type="primary"
                      size="small"
                      @click="handleEdit(row)"
                    >
                      {{ $t("message.common.edit") }}
                    </el-button>
                    <el-button
                      v-if="canDeleteOrganization" 
                      link
                      type="danger"
                      size="small"
                      @click="handleDelete(row)"
                      :disabled="isRootNode(row)"
                    >
                      {{ $t("message.common.delete") }}
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <div class="pagination-container">
              <el-pagination
                v-model:current-page="queryParams.pageNum"
                v-model:page-size="queryParams.pageSize"
                :total="total"
                :page-sizes="[5, 10, 15, 20, 30]"
                :layout="'total, sizes, prev, pager, next, jumper'"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
              />
            </div>
          </div>        
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="50%"
      @closed="resetForm"
    >
      <el-form
        ref="formRef"
        :model="formData"
        :rules="rules"
        label-width="120px"
      >
        <el-form-item :label="$t('message.organization.name')" prop="name">
          <el-input
            v-model="formData.name"
            :placeholder="$t('message.organization.inputName')"
          />
        </el-form-item>

        <el-form-item
          :label="$t('message.organization.parentCompany')"
          prop="parent_id"
          v-if="!isEditingRoot"
        >

          <el-tree-select
            v-model="formData.parent_id"
            :props="treeProps"
            :load="loadTreeNode"
            lazy
            :placeholder="$t('message.organization.selectCompany')"
            check-strictly
            clearable
            filterable
            style="width: 100%"
            node-key="id"
          />

        </el-form-item>
        <el-form-item :label="$t('message.organization.parentCompany')" v-else>
          <el-input v-model="rootParentText" disabled />
        </el-form-item>

        <el-form-item
          :label="$t('message.organization.contact')"
          prop="contact"
        >
          <el-input
            v-model="formData.contact"
            type="textarea"
            :placeholder="$t('message.organization.inputContact')"
          />
        </el-form-item>
        <el-form-item :label="$t('message.organization.phone')" prop="phone">
          <el-input
            v-model="formData.phone"
            type="textarea"
            :placeholder="$t('message.organization.inputPhone')"
          />
        </el-form-item>

        <el-form-item
          :label="$t('message.organization.location')"
          prop="location"
        >
          <el-input
            v-model="formData.location"
            type="textarea"
            :placeholder="$t('message.organization.inputLocation')"
          />
        </el-form-item>

        <el-form-item
          :label="$t('message.organization.describe')"
          prop="describe"
        >
          <el-input
            v-model="formData.describe"
            type="textarea"
            :placeholder="$t('message.organization.inputDescribe')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">{{
            $t("message.common.cancel")
          }}</el-button>
          <el-button type="primary" @click="submitForm">{{
            $t("message.common.confirm")
          }}</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useI18n } from "vue-i18n";
import {
  getCompanyTree,
  getCompanyList,
  getCompanyAdd,
  getCompanyEdit,
  getCompanyDelete,
} from "@/api/CompanyManager";
import { usePermission } from '@/composables/usePermission'

const { canAddOrganization,canEditOrganization,canDeleteOrganization } = usePermission()

const { t } = useI18n();

// Tree structure data
const treeData = ref([]);



// Query parameters
const queryParams = ref({
  pageNum: 1,
  pageSize: 10,
  name: "",
  companyId: null,
});

/* //查询企业树
const queryCompanyTreeParams = reactive({
companyId: null
})
 */
// Table data
const organizationList = ref([]);

const loading = ref(false);
const total = ref(3);
//const jumperPage = ref(1)
// 对话框控制
const dialogVisible = ref(false);
const dialogTitle = ref("");
const companyTreeData = ref([]);
const currentParentId = ref(0);

const formRef = ref();
const isEditingRoot = computed(() => formData.id  ||  formData.isRootNode);
const rootParentText = ref(t("message.organization.rootCompany"));

// 树形配置
const treeProps = {
  value: "id",
  label: "name",
  children: "children",
};

// 表单数据
const formData = reactive({
  id: 0,
  name: "",
  parent_id: 0,
  company_id: 0,
  describe: "",
  contact: "",
  phone: "",
  location: "",
  isRootNode: 1,
  created_at: null,
});

// 表单验证规则
const rules = reactive({
  name: [
    {
      required: true,
      message: t("message.organization.nameRequired"),
      trigger: "blur",
    },
  ],
  parent_id: [
    {
      required: true,
      message: t("message.organization.companyRequired"),
      trigger: "blur",
    },
  ],
});

const fetchData = async () => {
  loading.value = true;
  try {
    // 创建干净的请求参数对象，移除空值
    const params = {
      pageNum: queryParams.value.pageNum,
      pageSize: queryParams.value.pageSize,
      ...(queryParams.value.name && { name: queryParams.value.name }), // 只有name有值时才包含
      ...(queryParams.value.companyId && {
        companyId: queryParams.value.companyId,
      }), // 只有companyId有值时才包含
    };
    await loadCompanyList(params);
  } catch (error) {
    ElMessage.error(t("message.common.fetchError"));
  } finally {
    loading.value = false;
  }
};



// 判断是否是根节点的方法
const isRootNode = (node) => {
  return node.isRootNode === true;
};

// 在 loadCompanyTree 函数中，添加错误处理
const loadCompanyTree = async () => {
  try {
    loading.value = true;
 
    
    const params = {isGetChild:false}
    const res = await getCompanyTree({params});

    if (res?.success) {
      companyTreeData.value = res.data.list;
      treeData.value = transformTreeData(res.data.list || []);
      if ( res.data.list.length > 0) {
        formData.parent_id =  res.data.list[0].id;
        queryParams.value.companyId = res.data.list[0].id
        formData.company_id =  res.data.list[0].id;
        await fetchData();
      }
    } else {
      ElMessage.error(res?.msg || t("message.gateway.loadCompanyFailed"));
    }
    
  } catch (error) {
    console.error("加载企业树失败:", error);
    ElMessage.error(t("message.gateway.loadCompanyFailed"));
  } finally {
    loading.value = false;
  }
};

const loadCompanyList = async (params = {}) => {
  try {
    //企业列表
    const resList = await getCompanyList(params);
    if (resList?.success) {
      organizationList.value = resList.data.list;
      total.value = resList.data.totalCount;

      if (organizationList.value.length === 0) {
        ElMessage.warning(t("message.organization.noData"));
      }
    } else {
      ElMessage.error(resList?.msg || t("message.gateway.loadCompanyFailed"));
    }
  } catch (error) {
    console.error("加载企业树失败:", error);
    ElMessage.error(t("message.gateway.loadCompanyFailed"));
  } finally {
    loading.value = false;
  }
};

 
const loadTreeNode = async (node, resolve) => {
  try {
    await new Promise(resolve => setTimeout(resolve, 50)); 
    // 首次加载根节点（level=0）
    if (node.level === 0) {
      const res = await getCompanyTree({ 
        isGetChild: false
      });
      const rootNodes = transformTreeData(res.data.list || []);
      resolve(rootNodes);
      return;
    }

    // 加载子节点
    const res = await getCompanyTree({
      isGetChild: false,
      companyId: node.data.id
    });
    
    const childNodes = transformTreeData(res.data.list || []);
    resolve(childNodes);

  } catch (error) {
    console.error('加载企业树失败:', error);
    resolve([]);
  }
}  

/* 
const loadNode = async (node, resolve) => {
  try {
      //node.loading = true;
         // 添加防抖避免快速连续加载
    await new Promise(resolve => setTimeout(resolve, 50)); 
    // 首次加载根节点（level=0）
    if (node.level === 0) {
      const res = await getCompanyTree({ 
        isGetChild: false
      });
      formData.parent_id =  res.data.list[0].id;
      const rootNodes = transformTreeData(res.data.list || []);
      //companyTreeData.value = res.data.list;
      rootNodes.forEach(root => {
        if (root.children && root.children.length > 0) {
          
          root.isLeaf = false;
          root.hasLoaded = true;
          root.expanded = true; 
        }
      });      

      resolve(rootNodes);
      // 自动展开第一级节点
      nextTick(() => {
        if (rootNodes.length > 0 && treeRef.value) {
          treeRef.value.setCurrentKey(rootNodes[0].id);
        }
      });
      return; 
    }

    // 如果是第一级节点（level=1）且已经加载过子节点
    if (node.level === 1 && node.data.hasLoaded) {
      resolve(node.data.children || []);      
      return;
    }

    // 其他情况（加载更深层级的节点）
    const res = await getCompanyTree({
      isGetChild: false,
      companyId: node.data.id
    });
    
    const childNodes = transformTreeData(res.data.list || []);
    childNodes.forEach(child => {
      if (child.children && child.children.length > 0) {
        child.isLeaf = false;
        child.hasLoaded = true;
      }
    });
    
    resolve(childNodes);
    
  } catch (error) {
    console.error('加载企业树失败:', error);
    resolve([]);
  }
  finally {
    //node.loading = false;
  }
}  */


// Transform API data to tree structure
const transformTreeData = (apiData) => {
  return apiData.map((item) => ({
    id: item.id,
    label: item.name, // Or use t() if you need translation
     name: item.name,
    parent_id: item.parent_id,
    level: item.level,
    order_num: item.order_num,
    contact: item.contact,
    phone: item.phone,
    location: item.location,
    created_at: item.created_at,
    children: item.children ? transformTreeData(item.children) : [],
  }));
};

// Recursively flatten hierarchical data

// Helper function to format date
const formatDateTime = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    return date
      .toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(/\//g, "-");
  } catch {
    return dateString;
  }
};
 
// 企业选择变化
const handleCompanyChange = (selectedCompanyId) => {
  queryParams.value.companyId = selectedCompanyId;
};


const handleNodeClick =async (nodeData) => {
  
  // 实际业务逻辑示例：
  // 1. 加载该节点对应的数据
  //loadNodeData(nodeData.id)
  
  // 2. 更新查询参数 // 根据选中节点刷新右侧表格数据
  queryParams.value.companyId = nodeData.id
  fetchData()

}
 
const handleQuery = () => {
  fetchData();
};

const handleSizeChange = (val) => {
  queryParams.value.pageNum = 1;
  queryParams.value.pageSize = val;
  fetchData();
};

const handleCurrentChange = (val) => {
  queryParams.value.pageNum = val;
  fetchData();
};


const handleAdd = () => {
  dialogTitle.value = t("message.organization.add");
  formData.id = 0;
   if (companyTreeData.value.length > 0) {
    formData.parent_id = companyTreeData.value[0].id;
  } 
  formData.isRootNode = 0;
  dialogVisible.value = true;
};

const handleEdit = (row) => {
  dialogTitle.value = t("message.organization.edit");
  Object.assign(formData, row);

  if (row.isRootNode) {
    currentParentId.value = row.parent_id;
    
    // 如果是根节点，清空parent_id并显示为顶级
    formData.parent_id = null;
    rootParentText.value = t("message.organization.rootCompany");
  } else {
    currentParentId.value = row.parent_id;

    formData.parent_id = row.parent_id;
    rootParentText.value = row.parent_name;
  }

  dialogVisible.value = true;
};

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      t("message.organization.deleteConfirm", { name: row.name }),
      t("message.common.warning"),
      {
        confirmButtonText: t("message.common.confirm"),
        cancelButtonText: t("message.common.cancel"),
        type: "warning",
      }
    );

    // 用户确认删除
    loading.value = true;
    const requestData = {
      id: row.id
    };

    const ret = await getCompanyDelete(requestData);

    if (ret.success) {
      ElMessage.success(t("message.organization.deleteSuccess"));
      await loadCompanyTree(); // 刷新数据
    } else {            
      ElMessage.error(ret?.msg);       
    }
  } catch (error) {
    // 区分用户取消和真实错误
    if (error !== "cancel") {
      console.error("删除企业失败:", error);
      ElMessage.error(t("message.organization.operationFailed"));
    }
    // 用户点击取消不做任何处理
  } finally {
    loading.value = false;
  }
};

const submitForm = async () => {
  try {
    await formRef.value?.validate();

    const requestData = {
      id: formData.id,
      name: formData.name,
      // 如果是根节点，不传parent_id或传0
      parent_id: isEditingRoot.value
        ? currentParentId.value
        : formData.parent_id,
      describe: formData.describe,
      contact: formData.contact,
      phone: formData.phone,
      location: formData.location,
    };

    if (formData.id) {
      const ret = await getCompanyEdit(requestData);
      if (ret.success) {
        ElMessage.success(t("message.organization.updateSuccess"));
      }
      else
      {        
        ElMessage.error(ret?.msg || t("message.organization.updateFailed"));
      }
    } else {
      const ret = await getCompanyAdd(requestData);
      if (ret.success) {
        ElMessage.success(t("message.organization.addSuccess"));
      } else {        
        ElMessage.error(ret?.msg || t("message.organization.addFailed"));
      }
    }

    dialogVisible.value = false;
    await loadCompanyTree();
  } catch (error) {
    console.error("表单提交失败:", error);
    ElMessage.error(error.message || t("message.common.operationFailed"));
  }
};

onMounted(async () => {
  await loadCompanyList();
  await loadCompanyTree();
});
</script>

<style scoped lang="scss">
.organization-container {
  padding: 20px;

  .el-row {
    display: flex;
    align-items: stretch; // 关键属性，让子项高度相同

    .el-col {
      display: flex;
      flex-direction: column;

      .tree-container,
      .right-content {
        flex: 1; // 填充剩余空间
        height: 100%; // 确保高度100%
      }
    }
  }

  .box-card {
    border-radius: 4px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-title {
        font-size: 16px;
        font-weight: bold;
      }
    }
  }

  .equal-height-row {
    display: flex;
    align-items: stretch; // 确保列高度相同
    height: 800px; //calc(100vh - 180px); // 根据实际布局调整，减去的值=顶部高度+卡片边距等

    .equal-height-col {
      display: flex;
      flex-direction: column;
      height: 100%;

      .tree-container,
      .right-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden; // 禁止外部容器滚动
      }
    }
  }

  .tree-container {
    // 保持原有样式
    padding: 15px;
    background-color: #fff;  // #f5f7fa;
    border-radius: 4px;
    height: 100%;
    border: 1px solid #ebeef5;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    // 新增滚动相关样式
    display: flex;
    flex-direction: column;
    overflow: hidden; // 隐藏内部溢出

    .tree-title {
      margin-top: 0;
      margin-bottom: 15px;
      font-size: 14px;
      color: #606266;
      flex-shrink: 0; // 防止标题被压缩
    }

    // 树容器滚动区域
    .tree-scroll-wrapper {
      flex: 1;
      overflow: auto; // 自动显示滚动条
      min-height: 200px; // 最小高度保证

      // 自定义滚动条样式 (Webkit浏览器)
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background-color: #c1c1c1;
        border-radius: 4px;
        &:hover {
          background-color: #a8a8a8;
        }
      }
      &::-webkit-scrollbar-track {
        background-color: #f1f1f1;
        border-radius: 4px;
      }
    }

    :deep(.el-tree) {
      background-color: transparent;
      min-width: 100%; // 保证树的最小宽度
      width: max-content; // 根据内容自动扩展宽度

      .tree-node {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-right: 8px;
        min-width: 100%; // 节点最小宽度
        
          &.is-current {
            > .el-tree-node__content {
              background-color: #f0f7ff;
              .tree-node-label {
                color: #409eff;
                font-weight: 500;
              }
            }
          }
        .node-actions {
          visibility: hidden;
        }

        &:hover .node-actions {
          visibility: visible;
        }
      }
    }
  }

  .right-content {
    padding-left: 0;
    margin-left: -10px;
    border: 1px solid #ebeef5; // 添加边框线
    border-radius: 4px; // 圆角
    padding: 15px; // 增加内边距
    background-color: #fff; // 白色背景
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1); // 可选：添加阴影

    .search-area {
      margin-bottom: 20px;

      :deep(.el-form-item) {
        margin-bottom: 0;
      }
    }

    .table-wrapper {
      width: 100%;
      margin-bottom: 20px;
      flex: 1;
      overflow-y: auto; // 表格区域单独滚动

      :deep(.el-table) {
        width: 100% !important;
        border: 1px solid #ebeef5; // 表格外边框
        border-radius: 4px; // 圆角

        th,
        td {
          border-right: 1px solid #ebeef5; // 单元格右边框
          border-bottom: 1px solid #ebeef5; // 单元格下边框
        }
      }
    }

    .search-area,
    .pagination-container {
      flex-shrink: 0; // 防止搜索和分页被压缩
    }
  }

  .pagination-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 20px;
  }
}
/* 添加禁用状态的样式 */
.node-actions-disabled {
  opacity: 0.5;
  cursor: not-allowed;

  .el-button {
    color: #c0c4cc !important;
  }
}
</style>
